import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import logger from '@ayocore/platform/common/helpers/logger';
import type { ReportingWeek } from '../../../schemas/reporting-week.schema';
import { isoWeekOfYear } from '../../../common/weekNumber';
import { ensureReportingWeek, useReportingWeekRepository } from '../../rpc';
import { useDashboardProjectId } from './useDashboardProjectId';

/** Имя query-параметра выбранной недели в URL дашборда (?week=18). */
const WEEK_PARAM = 'week';

/** '2030-01-07' → '07.01' (подпись недели в селекторе). */
export function formatDdMm(iso: string): string {
  const [, month, day] = iso.split('-');
  return `${day}.${month}`;
}

/**
 * Календарный номер недели — то, чем неделю называет верфь (ISO-неделя года),
 * а не поле `number` из БД (оно порядковое внутри проекта, см. common/weekNumber).
 * Принимает любую запись недели, в т.ч. связь `reporting_week` у акта.
 */
export function weekNo(week: { date_from: string }): number {
  return isoWeekOfYear(week.date_from);
}

/**
 * Подпись недели в селекторе: «13.07–19.07 · неделя 29».
 * Датами вперёд — по просьбе заказчика: недели ищут глазами по числам, а не по
 * номеру, поэтому первым идёт то, по чему сканируют список.
 */
export function weekLabel(week: ReportingWeek): string {
  return `${formatDdMm(week.date_from)}–${formatDdMm(week.date_to)} · неделя ${weekNo(week)}`;
}

/** Следующий день после ISO-даты (UTC-полночь — без TZ-сдвигов). */
function nextDayIso(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
}

interface WeekSelectionValue {
  /** Недели проекта (desc) — текущая и следующая гарантированно присутствуют. */
  weeks: ReportingWeek[];
  /** Выбранная неделя (из ?week, иначе текущая); null пока грузится/ошибка. */
  selectedWeek: ReportingWeek | null;
  /** Сменить выбранную неделю (пишет ?week, сохраняя прочие параметры). */
  setSelectedWeekId: (id: number | null) => void;
  /**
   * ТЕКУЩАЯ неделя ПО СЕРВЕРУ (TZ верфи Asia/Shanghai, server/lib/date.ts) —
   * потребители не вычисляют «сегодня» по часам браузера (Отчёты сравнивают
   * selectedWeek с ней, чтобы решить find-or-create vs lookup-only).
   */
  currentWeekId: number | null;
  loading: boolean;
  error: string | null;
  /** Перечитать недели — после дозаполнения диапазона задним числом. */
  reloadWeeks: () => Promise<void>;
}

const WeekSelectionContext = createContext<WeekSelectionValue | null>(null);

interface WeekSelectionProviderProps {
  children: ReactNode;
}

/**
 * Единый источник выбора отчётной недели для всех вкладок дашборда
 * (Заявки/Планы/Rolling Plan/Файлы/Отчёты). Раньше каждая вкладка держала свой
 * селектор и свой стейт — отсюда рассинхрон. Теперь:
 *
 *  - на открытии один раз find-or-create ТЕКУЩЕЙ недели (нумерация max+1) и
 *    СЛЕДУЮЩЕЙ (чтобы её всегда можно было выбрать для подачи заявок/планов
 *    заранее), грузит список недель проекта (desc);
 *  - выбранная неделя живёт в URL (?week=<id>) — переживает перезагрузку,
 *    шарится ссылкой, не плодит дубль вкладки (ключ app-таба = pathname без
 *    query). Нет/неизвестный ?week → ТЕКУЩАЯ неделя;
 *  - значение раздаётся через useWeekContext() — вкладки стали «тупыми»
 *    потребителями без собственной загрузки.
 *
 * Повторный RPC при быстром ре-маунте гасится ref-гардом.
 */
export function WeekSelectionProvider({ children }: WeekSelectionProviderProps) {
  const projectId = useDashboardProjectId();
  const weekRepository = useReportingWeekRepository();
  const [searchParams, setSearchParams] = useSearchParams();
  const [weeks, setWeeks] = useState<ReportingWeek[]>([]);
  const [currentWeekId, setCurrentWeekId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestedRef = useRef(false);

  useEffect(() => {
    if (requestedRef.current || Number.isNaN(projectId)) return;
    requestedRef.current = true;
    let active = true;
    const load = async () => {
      try {
        // Порядок важен: сначала текущая (номер max+1), потом следующая.
        // project_id — чтобы недели создавались именно у этого проекта.
        const current = await ensureReportingWeek({ project_id: projectId });
        await ensureReportingWeek({ project_id: projectId, date: nextDayIso(current.dateTo) });
        const list = await weekRepository
          .filterByProject(projectId)
          .orderBy('date_from', 'desc')
          .fetch();
        if (!active) return;
        setWeeks(list);
        setCurrentWeekId(current.reportingWeekId);
      } catch (err) {
        if (!active) return;
        setError(err instanceof Error ? err.message : 'Не удалось загрузить недели');
        logger.error(err, '[mws] WeekSelectionProvider');
      } finally {
        if (active) setLoading(false);
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, [projectId, weekRepository]);

  // Выбранная неделя из ?week с фолбэком на текущую (id из URL, которого нет в
  // списке — например устаревшая ссылка/другой проект — игнорируем).
  const weekParam = searchParams.get(WEEK_PARAM);
  const parsedId = weekParam != null && weekParam !== '' ? Number(weekParam) : null;
  const validId = parsedId != null && weeks.some(w => w.id === parsedId) ? parsedId : null;
  const selectedWeekId = validId ?? currentWeekId;
  const selectedWeek = weeks.find(w => w.id === selectedWeekId) ?? null;

  const setSelectedWeekId = useCallback(
    (id: number | null) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          if (id == null) next.delete(WEEK_PARAM);
          else next.set(WEEK_PARAM, String(id));
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  /** Перечитать список недель — после дозаполнения диапазона задним числом. */
  const reloadWeeks = useCallback(async () => {
    const list = await weekRepository
      .filterByProject(projectId)
      .orderBy('date_from', 'desc')
      .fetch();
    setWeeks(list);
  }, [weekRepository, projectId]);

  const value: WeekSelectionValue = {
    weeks,
    selectedWeek,
    setSelectedWeekId,
    currentWeekId,
    loading,
    error,
    reloadWeeks,
  };
  return <WeekSelectionContext.Provider value={value}>{children}</WeekSelectionContext.Provider>;
}

/** Доступ к выбранной неделе дашборда. Только внутри <WeekSelectionProvider>. */
export function useWeekContext(): WeekSelectionValue {
  const ctx = useContext(WeekSelectionContext);
  if (!ctx) throw new Error('useWeekContext: компонент вне <WeekSelectionProvider>');
  return ctx;
}

/**
 * Не-кидающий доступ к контексту недели: возвращает null вне <WeekSelectionProvider>.
 * Для компонентов, которые работают и во вкладке дашборда (есть выбранная неделя),
 * и на отдельной странице (провайдера нет) — напр. список инспекций.
 */
export function useWeekContextOptional(): WeekSelectionValue | null {
  return useContext(WeekSelectionContext);
}
